import { useNavigate } from "react-router-dom";
import { ChevronRight, Hourglass } from "lucide-react";
import { useAuth } from "@/core/hooks/useAuth";
import { useMeusEmprestimos } from "@/modules/biblioteca/hooks/useEmprestimos";
import {
  escolherLivroEmMaos,
  textoIncentivo,
  textoOutros,
  textoPrazo,
} from "@/modules/biblioteca/utils/livroEmMaos";
import { dataHojeInput } from "@/shared/lib/datas";

/**
 * "Seu livro em mãos" na Início (aprovado em 02/11/2026, protótipo "Início com mais vida"):
 * para quem está com um livro emprestado, mostra o título, o prazo de devolução e uma frase curta.
 * Vale para qualquer papel (usa só os empréstimos da própria pessoa). Só aparece se a pessoa
 * enxerga a Biblioteca e tem livro em mãos; enquanto carrega ou sem livro, a seção fica oculta
 * (mesma regra dos outros previews da Início). O toque leva à Biblioteca da pessoa.
 */
export function CartaoLivroEmMaos() {
  const navigate = useNavigate();
  const { pessoa, temAcessoAdministrativo, modulosVisiveis } = useAuth();
  const veBiblioteca = modulosVisiveis.includes("biblioteca");
  const { data } = useMeusEmprestimos(veBiblioteca ? pessoa?.id : undefined);

  const livro = veBiblioteca ? escolherLivroEmMaos(data ?? [], dataHojeInput()) : null;
  if (!livro) return null;

  const destino = temAcessoAdministrativo ? "/admin/visualizar-como-usuario" : "/usuario";
  const incentivo = textoIncentivo(livro.dias);
  const outros = textoOutros(livro.outros);
  // Cor do ícone acompanha a urgência: azul/verde no prazo, amarelo nos últimos dias, vermelho vencido.
  const tom =
    livro.dias !== null && livro.dias < 0
      ? "bg-danger/10 text-danger"
      : livro.dias !== null && livro.dias <= 1
        ? "bg-warning/10 text-warning"
        : "bg-primary/10 text-primary";

  return (
    <button
      type="button"
      onClick={() => navigate(destino)}
      className="mb-4 flex w-full items-center gap-3 rounded-2xl border border-border bg-surface p-4 text-left transition-colors hover:border-primary sm:p-5"
    >
      <span className={`grid h-11 w-11 shrink-0 place-items-center rounded-xl ${tom}`}>
        <Hourglass size={22} aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[11px] font-bold uppercase tracking-wide text-muted">
          Seu livro em mãos
        </span>
        <span className="mt-0.5 block break-words text-sm font-bold text-text">{livro.titulo}</span>
        <span className="mt-0.5 block text-xs font-medium text-text">
          {textoPrazo(livro.dias, livro.prazo)}
        </span>
        {incentivo && <span className="block text-xs text-muted">{incentivo}</span>}
        {outros && <span className="mt-1 block text-xs text-muted">{outros}</span>}
      </span>
      <ChevronRight size={16} className="shrink-0 text-muted" aria-hidden="true" />
    </button>
  );
}
